import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAssistanceContext } from './AssistanceContext';
import { useTranslation } from 'react-i18next';
import classnames from '../../../core/utils/classnames.tsx';
import { withIcon } from '../../../core/components/Icon';
import SmartInboxSvg from '../../../../../static/images/smart_inbox.svg';
import {
    faArrowDownToBracket,
    faArrowUpRightFromSquare,
    faArrowUpToLine,
    faChevronDown,
    faChevronUp,
    faEnvelope,
    faFile,
    faPaperclip,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import Frame from '../../../core/components/Frame.tsx';
import DOMPurify from 'dompurify';
import { EmailAttachment as IEmailAttachment, PURIFY_CONFIG } from './components/EmailPreview.tsx';
import fontsStyle from '../../../../../stylesheets/fonts.css?inline';
import contentStyle from './components/email-content.css?inline';
import i18n from 'i18next';
import Button from '../../../core/components/Button';
import { useMutation, useQuery } from '@apollo/client';
import { DARK_MODE_LOCAL_STORAGE_KEY } from '../../../../App';
import Loader, { LoaderLine } from '../../../core/components/Loader.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import EmailEditor, { adjustEmail, DocumentEmail, EmailAvatar, EmailTemplate } from './components/EmailEditor';
import { GET_PROCESSING_RECORD_EMAILS, SEND_ASSISTANCE_EMAIL } from './queries.ts';

const FileIcon = withIcon(faFile);
const AttachmentIcon = withIcon(faPaperclip);
const EmailIcon = withIcon(faEnvelope);
const ChevronUpIcon = withIcon(faChevronUp);
const ChevronDownIcon = withIcon(faChevronDown);
const CloseIcon = withIcon(faXmark);
const DownloadIcon = withIcon(faArrowDownToBracket);
const ProcessIcon = withIcon(faArrowUpToLine);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

export const useDocumentEmailBody = (email: DocumentEmail | EmailTemplate) => {
    const attachmentsWithUrl = (email?.attachments || []).filter((attachment) => attachment?.url);
    const emailContent = DOMPurify.sanitize(email?.body, PURIFY_CONFIG) || '';

    return {
        content: emailContent,
        attachments: attachmentsWithUrl,
    };
};

const MAX_PREVIEW_RECEIVERS = 9;

export const EmailHeader = ({
    email,
    hasSmartInbox,
    emailBodySourcePart,
    isEmailBodySource,
    onProcessEmail,
    onGoToDocumentClick,
}: {
    email: DocumentEmail;
    hasSmartInbox?: boolean;
    emailBodySourcePart?: any;
    isEmailBodySource?: boolean;
    onProcessEmail?: () => void;
    onGoToDocumentClick?: (recordId: string) => void;
}) => {
    const { t } = useTranslation('assistance');

    const [isCollapsed, setIsCollapsed] = useState(true);
    const allReceivers = email.receivers.concat(email.cc || []).concat(email.bcc || []);

    const hideButton = (
        <Tooltip content={t('emailsView.email.hideDetails')} side="top">
            <button
                className="text-sm text-content-medium px-0.5 hover:text-content-strong transition-colors ml-1 translate-y-px"
                onClick={() => setIsCollapsed(true)}
            >
                <ChevronUpIcon />
            </button>
        </Tooltip>
    );

    return (
        <div className="flex gap-4 justify-between items-start">
            <div className="flex flex-col gap-2 w-full">
                <div className="flex gap-1 items-center">
                    <span className="font-medium text-content-strong">{email.sender.name}</span>
                    <span className="text-content-medium text-sm">&lt;{email.sender.email}&gt;</span>
                    {hasSmartInbox && (
                        <Tooltip
                            content={t('overview.filters.smartInboxTooltip')}
                            className="w-[170px] text-center"
                            side="top"
                        >
                            <img src={SmartInboxSvg} className="self-center w-[18px] h-[18px] mb-[1px]" />
                        </Tooltip>
                    )}

                    <span className="block text-content-medium text-sm ml-auto">
                        {new Date(email.createdAt ? email.createdAt : email.receivedAt).toLocaleString(i18n.language)}
                    </span>
                </div>
                <div className="flex gap-5">
                    <div className="text-content-medium text-sm flex flex-col gap-0 leading-relaxed flex-1">
                        {isCollapsed ? (
                            <span>
                                {t('emailsView.email.to')}:{' '}
                                {allReceivers
                                    .slice(0, MAX_PREVIEW_RECEIVERS)
                                    .map((r) => r.email.split('@')[0])
                                    .join(', ')}
                                {allReceivers.length > MAX_PREVIEW_RECEIVERS &&
                                    ` ${t('emailsView.email.moreReceivers', {
                                        count: allReceivers.length - MAX_PREVIEW_RECEIVERS,
                                    })}`}
                                <Tooltip content={t('emailsView.email.showDetails')} side="top">
                                    <button
                                        className="text-sm text-content-medium px-0.5 hover:text-content-strong transition-colors ml-1"
                                        onClick={() => setIsCollapsed(false)}
                                    >
                                        <ChevronDownIcon />
                                    </button>
                                </Tooltip>
                            </span>
                        ) : (
                            <>
                                <span className="flex gap-1">
                                    <span className="flex-none w-9">{t('emailsView.email.to')}:</span>
                                    <span>
                                        {email.receivers.map((r) => r.email).join(', ')}
                                        {!email.cc && !email.bcc && hideButton}
                                    </span>
                                </span>
                                {email.cc && (
                                    <span className="flex gap-1">
                                        <span className="flex-none w-9">{t('emailsView.email.cc')}:</span>
                                        <span>
                                            {email.cc.map((r) => r.email).join(', ')}
                                            {!email.bcc && hideButton}
                                        </span>
                                    </span>
                                )}
                                {email.bcc && (
                                    <span className="flex gap-1">
                                        <span className="flex-none w-9">{t('emailsView.email.bcc')}:</span>
                                        <span>
                                            {email.bcc.map((r) => r.email).join(', ')}
                                            {hideButton}
                                        </span>
                                    </span>
                                )}
                            </>
                        )}
                    </div>

                    {!isEmailBodySource &&
                        emailBodySourcePart &&
                        emailBodySourcePart.recordId &&
                        onGoToDocumentClick && (
                            <button
                                onClick={() => onGoToDocumentClick(emailBodySourcePart.recordId)}
                                className="flex items-center gap-1 text-content-brand-default hover:text-content-brand-hover text-sm cursor-pointer"
                            >
                                {t('attachments.goToDocument')} <ExternalLinkIcon />
                            </button>
                        )}

                    {!isEmailBodySource && emailBodySourcePart && !emailBodySourcePart.recordId && onProcessEmail && (
                        <button
                            onClick={onProcessEmail}
                            className="flex items-center gap-1 text-content-brand-default hover:text-content-brand-hover text-sm cursor-pointer"
                        >
                            <ProcessIcon /> {t('attachments.processEmail')}
                        </button>
                    )}

                    {email.sourceUrl && (
                        <Tooltip content={t('attachments.downloadEmail')} side="top">
                            <a
                                className="flex items-center gap-1 text-content-medium hover:text-content-brand-hover text-sm"
                                href={email.sourceUrl}
                                target="_blank"
                            >
                                <DownloadIcon />
                            </a>
                        </Tooltip>
                    )}
                </div>
            </div>

            {email.attachments.length > 0 && (
                <div className="flex gap-2 items-center text-content-medium text-sm mt-1.5">
                    <AttachmentIcon />
                </div>
            )}
        </div>
    );
};

const EmailContent = ({
    subject,
    content,
    attachments,
}: {
    subject: string;
    content: string;
    attachments: IEmailAttachment[];
}) => {
    const isDarkMode = !!localStorage.getItem(DARK_MODE_LOCAL_STORAGE_KEY);

    return (
        <div className="rounded-xl flex flex-col gap-5 p-7 border border-line-weak border-solid bg-surface-weak">
            <div className="font-medium text-content-strong">{subject}</div>
            <div className="flex flex-col gap-4 leading-relaxed">
                <Frame>
                    <style>
                        {fontsStyle.replaceAll('../', '/')}
                        {contentStyle}
                    </style>
                    <div
                        className={classnames('email__html', isDarkMode && 'email__html--dark-mode')}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                </Frame>
            </div>

            {attachments.length > 0 &&
                attachments.map((attachment, index) => <EmailAttachment key={index} attachment={attachment} />)}
        </div>
    );
};

const EmailAttachment = ({ attachment }: { attachment: IEmailAttachment }) => {
    return (
        <a
            className="flex gap-3 items-center bg-surface-strong !text-content-strong px-4 py-3 border border-line-weak border-solid rounded-md hover:bg-surface-strong hover:border-line-strong cursor-pointer transition-colors"
            target="_blank"
            href={attachment.url}
        >
            <FileIcon />
            <span className="text-content-strong">{attachment.name}</span>
        </a>
    );
};

const Email = ({ email, hasSmartInbox }: { email: DocumentEmail; hasSmartInbox?: boolean }) => {
    const { content, attachments } = useDocumentEmailBody(email);
    return (
        <div className="flex gap-3">
            <EmailAvatar emailAddress={email.sender} />

            <div className="flex flex-col gap-4 flex-1">
                <EmailHeader email={email} hasSmartInbox={hasSmartInbox} />
                <EmailContent subject={email.subject} content={content} attachments={attachments} />
            </div>
        </div>
    );
};

const EmailLoader = () => {
    return (
        <div className="flex gap-3">
            <div className="flex flex-none border border-solid border-line-weak items-center justify-center w-8 h-8 bg-surface-weak rounded-full text-content-strong font-medium text-sm -mt-1.5" />

            <div className="flex flex-col gap-4 flex-1">
                <div className="flex flex-col gap-4 flex-1">
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-1 items-center">
                            <span className="font-medium text-content-strong w-32">
                                <LoaderLine />
                            </span>
                            <span className="text-content-medium text-sm w-32">
                                <LoaderLine />
                            </span>
                        </div>
                    </div>

                    <div className="rounded-xl flex flex-col gap-5 p-7 border border-line-weak border-solid bg-surface-weak">
                        <Loader className="p-0 m-0" />
                    </div>
                </div>
            </div>
        </div>
    );
};

const EmailsView = React.memo(({ containerRef }: { containerRef?: React.RefObject<HTMLDivElement> }) => {
    const { t } = useTranslation('assistance');

    const [isFormVisible, setIsFormVisible] = useState(false);
    const [isAnimationComplete, setIsAnimationComplete] = useState(false);

    const { record } = useAssistanceContext();
    const {
        data: emailsData,
        loading: emailsLoading,
        refetch: emailsRefetch,
    } = useQuery(GET_PROCESSING_RECORD_EMAILS, {
        variables: {
            documentType: record?.documentType,
            recordId: record?.id,
        },
        skip: !record?.id,
    });
    const originalEmail: DocumentEmail = emailsData?.documentEmails?.received;
    const sentEmails: DocumentEmail[] = emailsData?.documentEmails?.sent || [];
    const hasSmartInbox =
        record?.createdSource === 'SMART_INBOX' || record?.createdSource === 'SMART_INBOX_MANUAL_CREATION';

    useEffect(() => {
        const element = containerRef?.current;
        if (!element) {
            setIsAnimationComplete(true);
            return;
        }

        const handleAnimationEnd = () => {
            setIsAnimationComplete(true);
        };

        element.addEventListener('animationend', handleAnimationEnd);

        const fallbackTimer = setTimeout(() => {
            setIsAnimationComplete(true);
        }, 500);

        return () => {
            element.removeEventListener('animationend', handleAnimationEnd);
            clearTimeout(fallbackTimer);
        };
    }, [containerRef]);

    useEffect(() => {
        if (isAnimationComplete && record?.id && !emailsLoading && sentEmails.length === 0) {
            setIsFormVisible(true);
        }
    }, [isAnimationComplete, record?.id, emailsLoading, sentEmails]);

    const [sendAssistanceEmail] = useMutation(SEND_ASSISTANCE_EMAIL);
    const handleSubmit = async (email: DocumentEmail) => {
        await sendAssistanceEmail({
            variables: {
                documentType: record?.documentType,
                recordId: record.id,
                email: email,
            },
        });
        return await emailsRefetch();
    };

    return (
        <div className="space-y-4">
            {(!record?.id || emailsLoading || !isAnimationComplete) && <EmailLoader />}

            {sentEmails.map((email, index) => (
                <Email key={index} email={adjustEmail(email)} hasSmartInbox={hasSmartInbox} />
            ))}

            {isFormVisible ? (
                <EmailEditor
                    record={record}
                    originalEmail={originalEmail}
                    receiverSuggestions={emailsData?.documentEmails?.receiverSuggestions}
                    sourceFile={emailsData?.documentEmails?.sourceFile}
                    onSubmit={async (email: DocumentEmail) => {
                        await handleSubmit(email);
                        setIsFormVisible(false);
                    }}
                    onCancel={() => setIsFormVisible(false)}
                />
            ) : (
                <div className="flex justify-end gap-2">
                    <Button
                        onClick={() => setIsFormVisible(true)}
                        className="flex gap-2 items-center"
                        disabled={!record?.id || emailsLoading}
                    >
                        <EmailIcon />
                        {t('emailsView.newEmailButton')}
                    </Button>
                </div>
            )}
        </div>
    );
});

export default EmailsView;
